(() => {
  if (window.__smProductCardReady) return;
  window.__smProductCardReady = true;

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const wishlistKey = "sm-wishlist-products";
  const galleryTimers = new WeakMap();

  const readWishlist = () => {
    try {
      return JSON.parse(window.localStorage.getItem(wishlistKey) || "[]");
    } catch (error) {
      return [];
    }
  };

  const writeWishlist = (items) => {
    window.localStorage.setItem(wishlistKey, JSON.stringify(Array.from(new Set(items))));
  };

  const syncWishlistButtons = (root = document) => {
    const items = readWishlist();
    const count = items.length;

    qsa("[data-sm-wishlist]", root).forEach((button) => {
      button.classList.toggle("is-active", items.includes(button.dataset.productHandle));
    });

    qsa("[data-sm-wishlist-count]").forEach((countNode) => {
      countNode.textContent = String(count);
      countNode.hidden = count === 0;
    });
  };

  const createToast = () => {
    let toast = qs("[data-sm-cart-toast]");
    if (toast) return toast;

    toast = document.createElement("aside");
    toast.className = "sm-cart-toast";
    toast.dataset.smCartToast = "";
    toast.innerHTML = `
      <div class="sm-cart-toast__bar"><span data-sm-cart-title>ADDED TO CART</span></div>
      <div class="sm-cart-toast__body">
        <button class="sm-cart-toast__close" type="button" aria-label="Close">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
        </button>
        <div class="sm-cart-toast__media"><img data-sm-cart-image alt="" width="82" height="82"></div>
        <a class="sm-cart-toast__link" href="/cart">VIEW CART</a>
      </div>
    `;
    document.body.appendChild(toast);
    qs(".sm-cart-toast__close", toast)?.addEventListener("click", () => toast.classList.remove("is-visible"));
    return toast;
  };

  let toastTimer;
  let cartAddLocked = false;
  const cartAddCooldown = 2600;

  const showCartToast = ({ title = "ITEM", image = "", success = true }) => {
    const toast = createToast();
    qs("[data-sm-cart-title]", toast).textContent = success ? `${title} ADDED TO CART` : title;
    const imageNode = qs("[data-sm-cart-image]", toast);
    const mediaNode = qs(".sm-cart-toast__media", toast);

    if (image) {
      imageNode.src = image;
      mediaNode.hidden = false;
    } else {
      imageNode.removeAttribute("src");
      mediaNode.hidden = true;
    }

    window.clearTimeout(toastTimer);
    toast.classList.add("is-visible");
    toastTimer = window.setTimeout(() => toast.classList.remove("is-visible"), 3500);
  };

  const updateCartBubbleLocally = (quantity = 1) => {
    qsa(".fg-header-only__cart span, .fg-header__cart span, .cart-count-bubble span[aria-hidden='true']").forEach((node) => {
      const currentCount = Number.parseInt(node.textContent || "0", 10);
      node.textContent = String((Number.isFinite(currentCount) ? currentCount : 0) + quantity);
    });
  };

  const setAddButtonsDisabled = (disabled) => {
    qsa("[data-sm-add-to-cart]").forEach((button) => {
      if (disabled) {
        button.setAttribute("data-sm-temporary-disabled", "true");
        button.setAttribute("disabled", "disabled");
      } else if (button.dataset.smTemporaryDisabled === "true") {
        button.removeAttribute("disabled");
        button.removeAttribute("data-sm-temporary-disabled");
      }
    });
  };

  const addButtonToCart = async (button) => {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;
    if (cartAddLocked) {
      return;
    }

    cartAddLocked = true;
    setAddButtonsDisabled(true);
    try {
      const quantity = Number(button.dataset.quantity || 1);
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", String(quantity));

      const response = await fetch("/cart/add.js", {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });
      const item = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(item.description || "Unable to add");

      showCartToast({
        title: button.dataset.smProductTitle || item.product_title || item.title || "ITEM",
        image: button.dataset.smProductImage || item.image || "",
        success: true,
      });
      updateCartBubbleLocally(quantity);
    } catch (error) {
      showCartToast({ title: "UNABLE TO ADD ITEM", image: "", success: false });
    } finally {
      window.setTimeout(() => {
        cartAddLocked = false;
        setAddButtonsDisabled(false);
      }, cartAddCooldown);
    }
  };

  const escapeHtml = (value = "") =>
    String(value).replace(/[&<>"']/g, (char) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "\"": "&quot;",
      "'": "&#39;",
    }[char]));

  const formatMoney = (cents = 0) => {
    const currency = window.Shopify?.currency?.active || "USD";
    const amount = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const fetchWishlistProduct = async (handle) => {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!response.ok) throw new Error("Product unavailable");
    return response.json();
  };

  const renderWishlistCard = (product) => {
    const image = product.featured_image || product.images?.[0] || "";
    const url = product.url || `/products/${product.handle}`;
    const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
    const addButton = variant
      ? `
        <button
          class="sm-wishlist-card__add"
          type="button"
          data-sm-add-to-cart
          data-variant-id="${escapeHtml(variant.id)}"
          data-quantity="1"
          data-sm-product-title="${escapeHtml(product.title)}"
          data-sm-product-image="${escapeHtml(image)}"
          ${variant.available ? "" : "disabled"}
        >
          ADD CART
        </button>
      `
      : "";

    return `
      <article class="sm-wishlist-card">
        <a class="sm-wishlist-card__media" href="${escapeHtml(url)}">
          ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : ""}
        </a>
        <div class="sm-wishlist-card__info">
          <h3><a href="${escapeHtml(url)}">${escapeHtml(product.title)}</a></h3>
          <p>${formatMoney(product.price)}</p>
          <div class="sm-wishlist-card__actions">
            <button class="sm-wishlist-card__remove" type="button" data-sm-remove-wishlist data-product-handle="${escapeHtml(product.handle)}">
              REMOVE
            </button>
            ${addButton}
          </div>
        </div>
      </article>
    `;
  };

  const renderWishlistPage = async (root = document) => {
    const pages = qsa("[data-sm-wishlist-page]", root);
    if (!pages.length) return;

    const handles = readWishlist();

    pages.forEach(async (page) => {
      const grid = qs("[data-sm-wishlist-grid]", page);
      const empty = qs("[data-sm-wishlist-empty]", page);
      const status = qs("[data-sm-wishlist-status]", page);
      if (!grid) return;

      if (!handles.length) {
        grid.innerHTML = "";
        if (empty) empty.hidden = false;
        if (status) status.textContent = "";
        return;
      }

      if (empty) empty.hidden = true;
      if (status) status.textContent = "Loading wishlist...";

      const products = await Promise.all(handles.map((handle) => fetchWishlistProduct(handle).catch(() => null)));
      const availableProducts = products.filter(Boolean);

      grid.innerHTML = availableProducts.map(renderWishlistCard).join("");
      if (empty) empty.hidden = availableProducts.length > 0;
      if (status) status.textContent = availableProducts.length ? "" : "Wishlist products are unavailable.";
    });
  };

  const showGalleryImage = (gallery, index) => {
    const images = qsa("[data-sm-hover-image]", gallery);
    if (!images.length) return;

    const activeIndex = (index + images.length) % images.length;
    gallery.dataset.smActiveIndex = String(activeIndex);
    images.forEach((image, imageIndex) => image.classList.toggle("is-active", imageIndex === activeIndex));
  };

  const getGallery = (node) => {
    const card = node.closest("[data-sm-product-card]");
    return card?.querySelector("[data-sm-hover-gallery]") || node.closest("[data-sm-hover-gallery]");
  };

  const startGallery = (source) => {
    const gallery = source?.matches?.("[data-sm-hover-gallery]") ? source : getGallery(source);
    if (!gallery) return;

    const images = qsa("[data-sm-hover-image]", gallery);
    if (images.length <= 1 || galleryTimers.has(gallery)) return;

    gallery.dataset.smActiveIndex = gallery.dataset.smActiveIndex || "0";
    const timer = window.setInterval(() => {
      const currentIndex = Number(gallery.dataset.smActiveIndex || 0);
      showGalleryImage(gallery, currentIndex + 1);
    }, 780);

    galleryTimers.set(gallery, timer);
  };

  const stopGallery = (source) => {
    const gallery = source?.matches?.("[data-sm-hover-gallery]") ? source : getGallery(source);
    if (!gallery) return;

    const timer = galleryTimers.get(gallery);
    if (timer) window.clearInterval(timer);
    galleryTimers.delete(gallery);
    showGalleryImage(gallery, 0);
  };

  document.addEventListener("click", (event) => {
    const currencyToggle = event.target.closest("[data-sm-currency-toggle]");
    if (currencyToggle) {
      event.preventDefault();

      const currency = currencyToggle.closest("[data-sm-currency]");
      const panel = currency?.querySelector("[data-sm-currency-panel]");
      const isOpen = currency?.classList.contains("is-open");

      qsa("[data-sm-currency].is-open").forEach((item) => {
        item.classList.remove("is-open");
        item.querySelector("[data-sm-currency-toggle]")?.setAttribute("aria-expanded", "false");
        const itemPanel = item.querySelector("[data-sm-currency-panel]");
        if (itemPanel) itemPanel.hidden = true;
      });

      if (currency && panel && !isOpen) {
        currency.classList.add("is-open");
        currencyToggle.setAttribute("aria-expanded", "true");
        panel.hidden = false;
      }
      return;
    }

    if (!event.target.closest("[data-sm-currency]")) {
      qsa("[data-sm-currency].is-open").forEach((item) => {
        item.classList.remove("is-open");
        item.querySelector("[data-sm-currency-toggle]")?.setAttribute("aria-expanded", "false");
        const panel = item.querySelector("[data-sm-currency-panel]");
        if (panel) panel.hidden = true;
      });
    }

    const addButton = event.target.closest("[data-sm-add-to-cart]");
    if (addButton) {
      event.preventDefault();
      event.stopPropagation();
      addButtonToCart(addButton);
      return;
    }

    const removeWishlistButton = event.target.closest("[data-sm-remove-wishlist]");
    if (removeWishlistButton) {
      event.preventDefault();
      event.stopPropagation();

      const handle = removeWishlistButton.dataset.productHandle;
      if (!handle) return;

      writeWishlist(readWishlist().filter((item) => item !== handle));
      syncWishlistButtons();
      renderWishlistPage();
      return;
    }

    const wishlistButton = event.target.closest("[data-sm-wishlist]");
    if (!wishlistButton) return;

    event.preventDefault();
    event.stopPropagation();

    const handle = wishlistButton.dataset.productHandle;
    if (!handle) return;

    const items = readWishlist();
    const nextItems = items.includes(handle) ? items : [...items, handle];
    writeWishlist(nextItems);
    syncWishlistButtons();
    renderWishlistPage();
  });

  document.addEventListener("mouseover", (event) => {
    const card = event.target.closest("[data-sm-product-card]");
    if (!card || card.contains(event.relatedTarget)) return;
    startGallery(card);
  });

  document.addEventListener("mouseout", (event) => {
    const card = event.target.closest("[data-sm-product-card]");
    if (!card || card.contains(event.relatedTarget)) return;
    stopGallery(card);
  });

  document.addEventListener("pointerover", (event) => {
    const card = event.target.closest("[data-sm-product-card]");
    if (!card || card.contains(event.relatedTarget)) return;
    startGallery(card);
  });

  document.addEventListener("pointerout", (event) => {
    const card = event.target.closest("[data-sm-product-card]");
    if (!card || card.contains(event.relatedTarget)) return;
    stopGallery(card);
  });

  document.addEventListener("focusin", (event) => {
    const card = event.target.closest("[data-sm-product-card]");
    if (card) startGallery(card);
  });

  document.addEventListener("focusout", (event) => {
    const card = event.target.closest("[data-sm-product-card]");
    if (card) stopGallery(card);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;

    qsa("[data-sm-currency].is-open").forEach((item) => {
      item.classList.remove("is-open");
      item.querySelector("[data-sm-currency-toggle]")?.setAttribute("aria-expanded", "false");
      const panel = item.querySelector("[data-sm-currency-panel]");
      if (panel) panel.hidden = true;
    });
  });

  syncWishlistButtons();
  renderWishlistPage();
  document.addEventListener("shopify:section:load", (event) => {
    syncWishlistButtons(event.target);
    renderWishlistPage(event.target);
  });
})();
